import {
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types';

import { categoryLabels } from './__test-helpers__/factories/Category';
import { APPROVAL_STATUSES } from './approval/constants';
import {
  AGREEMENT,
  ALL_ATTACHMENT_TYPES,
  COMMENT,
  OPPORTUNITY,
} from './attachments/constants';
import { invoiceSapStatuses, invoiceStatuses } from './balance/constants';
import { campaignLineStatuses } from './campaign-editor/constants';
import { CREATIVE_FILE_TYPES } from './campaign-line-creatives/constants';
import { allStatusesEnum as campaignStatuses } from './campaigns/constants';
import {
  approvalStatuses,
  changeStatuses,
  types as changeRequestTypes,
} from './change-requests/constants';
import { BUSINESSES, commercialIncentives } from './constants';
import { TASK_STATUSES } from './generic/TaskStatus/constants';
import { MEDIA_TYPES } from './generic/Thumbnail/constants';
import { merchantTypesOptions } from './merchants/constants';
import {
  allStagesEnum as opportunityStages,
  campaignObjectives,
  campaignTypes,
  invoiceBillingTypes,
} from './opportunities/constants';
import { productCodes, timeGranularities } from './products/constants';
import { genders as GENDERS } from './quotes/constants';

export const genderType = oneOf(GENDERS.map(option => option.value).concat(''));
export const gendersType = arrayOf(
  oneOf(GENDERS.map(option => option.value)).isRequired,
);

/* Domain specific shapes */

export const approvalProps = {
  id: number.isRequired,
  status: oneOf(Object.values(APPROVAL_STATUSES)).isRequired,
  type: string.isRequired,
  opportunityId: number.isRequired,
  quoteId: number,
  createdBy: string.isRequired,
  created: string.isRequired,
  lastModified: string,
  lastModifiedBy: string,
};

export const approvalShape = shape(approvalProps);

export const merchantProps = {
  id: number.isRequired,
  name: string.isRequired,
  merchantId: string.isRequired,
  isSelfService: bool.isRequired,
  merchant_id: string,
  eligibleForZMSAMPush: bool.isRequired,
  sapId: number,
  invoiceBillingEntity: object,
  businessModel: string.isRequired,
};

export const merchantShape = shape(merchantProps);

export const merchantAttributionProps = {
  kCode: string.isRequired,
  merchantName: string.isRequired,
  supplierName: string.isRequired,
  brandName: string.isRequired,
  brandCode: string.isRequired,
  merchantId: string.isRequired,
  merchantCountries: arrayOf(string).isRequired,
  supplierCountryName: string.isRequired,
  dwhModifiedDate: string.isRequired,
  dwhCreateDate: string.isRequired,
  gmv: number,
};

export const merchantAttributionShape = shape(merchantAttributionProps);

export const associatedBrandProps = {
  name: string.isRequired,
  code: string,
  id: number.isRequired,
  salesforceId: string,
};

export const associatedBrandShape = shape(associatedBrandProps);

export const associatedAccountProps = {
  id: number,
  name: string,
  creditorNumber: string,
  debitorNumber: string,
  taxNumber: string,
  businessPartnerId: string,
  eligibleForCampaigns: bool,
  eligibleForMonthlyBilling: bool,
  salesforceId: string,
};

export const associatedAccountShape = shape(associatedAccountProps);

export const associatedCampaignProps = {
  id: number.isRequired,
  name: string.isRequired,
  hasScalableBudget: bool.isRequired,
};

export const associatedCampaignShape = shape(associatedCampaignProps);

export const associatedOpportunityProps = {
  id: number.isRequired,
  name: string.isRequired,
  number: string.isRequired,
  campaignObjective: string,
  pool: string,
  salesChannel: string,
  salesUnit: string,
  salesTeam: string,
  useCase: string,
  stage: string,
  businessModel: string,
  billingType: string,
  salesforceId: string,
  reservationId: string,
  hasCCPCampaignLevelSteering: bool,
  accountSfId: string,
};

export const associatedOpportunityShape = shape(associatedOpportunityProps);

export const associatedQuoteProps = {
  id: number.isRequired,
  name: string.isRequired,
  purchaseOrderNumber: string,
  flexibilityTerms: string.isRequired,
  allowedFlexibilities: arrayOf(string.isRequired),
  commercialIncentive: string,
  targetKpi: string.isRequired,
  targetKpiValue: number.isRequired,
  grossOfferValue: number,
  campaignObjective: string,
  salesforceId: string,
};

export const associatedQuoteShape = shape(associatedQuoteProps);

export const associatedUserProps = {
  id: number.isRequired,
  firstName: string.isRequired,
  lastName: string.isRequired,
  number: string.isRequired,
  roles: arrayOf(string.isRequired),
  email: string,
  salesUnit: string,
  sapId: number,
};

export const associatedUserShape = shape(associatedUserProps);

export const associatedProductProps = {
  id: number.isRequired,
  name: string.isRequired,
  externalName: string.isRequired,
  code: string.isRequired,
  business: string.isRequired,
};

export const associatedProductShape = shape(associatedProductProps);

export const paymentTermsProps = {
  id: number,
  name: string,
  sapId: string,
  created: string,
  lastModified: string,
  createdBy: string,
  lastModifiedBy: string,
  isAvailableToUsers: bool,
};

export const paymentTermsShape = shape(paymentTermsProps);

// we would need this if we made users editable
// export const teamShape = shape({
//   id: number.isRequired,
//   name: string.isRequired,
//   mayApproveCreativeProducts: bool.isRequired,
// });

export const userProps = {
  id: number.isRequired,
  email: string.isRequired,
  firstName: string.isRequired,
  lastName: string.isRequired,
  mobile: string,
  number: string.isRequired,
  salesUnit: string.isRequired,
  salesTeam: string,
  roles: arrayOf(string.isRequired).isRequired,
  team: string,
  created: string.isRequired,
  lastModified: string.isRequired,
  createdBy: string.isRequired,
  lastModifiedBy: string.isRequired,
  sapId: number,
  isActive: bool,
  pool: string,
  uiVariant: string,
};

export const userShape = shape(userProps);

export const newUserShape = shape({
  id: number,
  email: string,
  firstName: string,
  lastName: string,
  mobile: string,
  number: string,
  salesUnit: string,
  roles: arrayOf(string.isRequired).isRequired,
  team: string,
  created: string,
  lastModified: string,
  createdBy: string,
  lastModifiedBy: string,
  sapId: number,
});

export const quarterlyBudgetShape = shape({
  id: number,
  agreement: number,
  year: number.isRequired,
  quarter: oneOf(['1', '2', '3', '4']).isRequired,
  share: number,
  created: string,
  lastModified: string,
  createdBy: string,
  lastModifiedBy: string,
});

export const agreementOpportunityProps = {
  id: number,
  agreement: number.isRequired,
  opportunity: number.isRequired,
  budget: number.isRequired,
  created: string,
  createdBy: string,
  lastModified: string,
  lastModifiedBy: string,
};
export const agreementOpportunityShape = shape(agreementOpportunityProps);

export const agreementProps = {
  id: number,
  name: string.isRequired,
  owner: associatedUserShape,
  ownerId: number,
  brandIds: arrayOf(number.isRequired),
  brands: arrayOf(associatedBrandShape),
  salesUnit: string,
  salesTeam: string,
  startDate: string.isRequired,
  endDate: string.isRequired,
  businessModel: string.isRequired,
  budget: number.isRequired,
  type: string.isRequired,
  negotiatedBy: string.isRequired,
  notes: string.isRequired,
  ciBudgetShare: number,
  creativeBudgetShare: number,
  mediaManagedBudgetShare: number,
  mediaSelfServiceBudgetShare: number,
  otherBudgetShare: number,
  quarterlyBudgets: arrayOf(quarterlyBudgetShape.isRequired),
  created: string,
  lastModified: string,
  createdBy: string,
  lastModifiedBy: string,
  agreedBudget: number,
  plannedBudget: number,
  pool: string,
  subPool: string,
  agreementOpportunities: arrayOf(agreementOpportunityShape.isRequired),
};
export const agreementShape = shape(agreementProps);

export const associatedAgreementProps = {
  id: number.isRequired,
  name: string.isRequired,
};

export const associatedAgreementShape = shape(associatedAgreementProps);

export const accountProps = {
  id: number,
  number: string,
  name: string.isRequired,
  type: string,
  creditorNumber: string,
  debitorNumber: string,
  businessPartnerId: string,
  paymentTermsId: number,
  mandatoryPoNumber: bool,
  created: string,
  lastModified: string,
  createdBy: string,
  lastModifiedBy: string,
  taxNumber: string,
  brandIds: arrayOf(number),
  addressStreet: string,
  addressHouseNumber: string,
  addressZip: string,
  addressCity: string,
  addressCountry: string,
  businessPartnerName: string,
  billingEmails: arrayOf(string.isRequired),
  iban: string,
  bic: string,
  sapStatus: string,
  eligibleForCampaigns: bool,
  sapAdditionalStatusInformation: string,
  eligibleForMonthlyBilling: bool,
  bankAccountNumber: string,
  bankAccountHolder: string,
  termsAndConditions: string,
  followers: arrayOf(associatedUserShape),
  salesforceId: string,
};
export const accountShape = shape(accountProps);

export const timeGranularityType = oneOf(
  [''].concat(Object.values(timeGranularities)),
);

export const targetSupplierProps = {
  id: number.isRequired,
  name: string,
  creditorNumber: string,
};

export const targetSupplierShape = shape(targetSupplierProps);

export const taskProps = {
  taskId: string.isRequired,
  status: oneOf(Object.values(TASK_STATUSES)).isRequired,
  taskName: string,
  lastModified: string.isRequired,
  result: object,
};

export const taskShape = shape(taskProps);

export const requirementShape = shape({
  id: number.isRequired,
  sizeType: string.isRequired,
  requirements: shape({
    fileType: oneOf(Object.keys(CREATIVE_FILE_TYPES)),
    device: oneOf(['Desktop', 'Mobile', 'Desktop & Mobile']),
    optional: bool,
    imageWidth: number,
    imageHeight: number,
    fileFormat: oneOfType([string, arrayOf(string.isRequired)]),
    fileSize: number,
    minDuration: number,
    maxDuration: number,
  }).isRequired,
});

export const mediaSetRequirementProps = {
  id: number.isRequired,
  name: string,
  productId: number.isRequired,
  requirementIds: arrayOf(number.isRequired).isRequired,
  created: string,
  createdBy: string,
  lastModified: string,
  lastModifiedBy: string,
};

export const mediaSetRequirementShape = shape(mediaSetRequirementProps);

export const basicProductProps = {
  id: number.isRequired,
  code: string.isRequired,
  business: oneOf(Object.values(BUSINESSES)).isRequired,
};

export const basicProductShape = shape(basicProductProps);

export const productProps = {
  adjustedFee: bool,
  defaultPrice: number,
  defaultPriceModelAwareness: string,
  defaultPriceModelEngagement: string,
  defaultPriceModelPerformance: string,
  defaultPriceModelOther: string,
  hasFreeService: bool,
  overwriteCategories: bool,
  overwriteGender: bool,
  overwriteTimeRange: bool,
  timeGranularity: timeGranularityType,
  possiblePriceModels: arrayOf(string).isRequired,
  serviceDescription: bool,
  hasCta: bool,
  hasLp: bool,
  hasTitle: bool,
  hasSubTitle: bool,
  hasText: bool,
  base14DayBudget: number,
  additional14DayBudgetPerCountry: number,
  ctaLimit: number,
  subtitleLimit: number,
  lpLimit: number,
  titleLimit: number,
  textLimit: number,
  business: string,
  code: string.isRequired,
  created: string,
  createdBy: string,
  deprecated: string,
  id: number.isRequired,
  lastModified: string,
  lastModifiedBy: string,
  name: string.isRequired,
  productGroup: string,
  creativeRequirements: arrayOf(requirementShape),
  supportsCreativeProductionBudgetBreakdown: bool,
  supportsCreativeEnhancedProductShootingBudgetBreakdown: bool,
  supportsCreativeLandingPageBudgetBreakdown: bool,
  supportsNumberOfURLs: bool,
  supportsCreativeCollabaryId: bool,
  supportsOutfitIds: bool,
  supportsCollectionIds: bool,
  adOpsLeadTime: number,
  exclusiveToSubscription: bool,
  externalName: string,
  frequencyCappingImpressions: number,
  frequencyCappingTimeUnit: string,
  frequencyCappingTimeUnitCount: number,
  genderSplitRule: object,
  guidelinesUrl: string,
  legacyPriceModels: arrayOf(string.isRequired, number.isRequired),
  minIntervalBetweenViewsTimeUnit: string,
  minIntervalBetweenViewsTimeUnitCount: number,
  notifyCollabarySelfServiceTeam: bool,
  operativeSystem: string,
  sortingRank: number,
  supportsHighlightedSKUs: bool,
  supportsCollabaryInvoicing: bool,
  requiresCountdown: bool,
  supportsAdManager: bool,
  supportsOfferTypes: bool.isRequired,
  supportsContentIds: bool,
  supportsTargeting: bool,
  supportsProductTags: bool.isRequired,
};

export const productShape = shape(productProps);

export const readOnlyProductProps = {
  ...productProps,
  creativeRequirements: string,
  supportsSkuVsBrandValidation: bool.isRequired,
  supportsContentIds: bool,
};

export const readOnlyProductShape = shape(readOnlyProductProps);

export const countryShape = shape({
  code: string,
  name: string.isRequired,
});

export const campaignLineTextBundleItemsProps = {
  country: string.isRequired,
  language: string.isRequired,
  title: string,
  subTitle: string,
  cta: string,
};

export const campaignLineTextBundleItemsShape = shape(
  campaignLineTextBundleItemsProps,
);

export const campaignLineTextBundleProps = {
  id: number.isRequired,
  name: string.isRequired,
  campaignLineTextBundleItems: arrayOf(campaignLineTextBundleItemsShape),
  created: string,
  createdBy: string,
  lastModified: string,
  lastModifiedBy: string,
  deprecated: string,
};

export const campaignLineTextBundleShape = shape(campaignLineTextBundleProps);

export const linkRefShape = shape({
  current: object, // eslint-disable-line react/forbid-prop-types
});

export const draftProps = {
  originValue: oneOfType([string, number, object]),
  draftValue: oneOfType([string, number, object]),
};

export const draftShape = shape(draftProps);

export const campaignLineProps = {
  id: number,
  name: string,
  countryCode: string.isRequired,
  status: oneOf(Object.values(campaignLineStatuses)),
  campaignId: number,
  product: oneOfType([basicProductShape, productShape]).isRequired,
  number: string,
  startDate: string,
  endDate: string,
  categories: arrayOf(string.isRequired),
  gender: genderType,
  language1: string,
  language2: string,
  language3: string,
  language4: string,
  title1: string,
  title2: string,
  title3: string,
  title4: string,
  cta1: string,
  cta2: string,
  cta3: string,
  cta4: string,
  desc1: string,
  desc2: string,
  desc3: string,
  desc4: string,
  landingPage1: string,
  landingPage2: string,
  landingPage3: string,
  landingPage4: string,
  created: string,
  createdBy: string,
  lastModified: string,
  lastModifiedBy: string,
  netClientBudget: number,
  netClientBudgetExternal: number,
  wkzBudget: number,
  coInvest: number,
  freeService: number,
  secondaryCountryCodes: arrayOf(string.isRequired),
  draftDiff: draftShape,
  archivedDraftDiff: draftShape,
  draftOrigin: number,
  isDraft: bool,
  isActiveDraft: bool,
  lastChangedToReady: string,
  opsHandoverCompliance: string,
  productTag: string,
  changeRequest: number,
  comment: string,
  originalFee: number,
  adjustedFee: number,
  pricing: string,
  subTitle1: string,
  subTitle2: string,
  subTitle3: string,
  subTitle4: string,
  quoteLineId: number,
  productId: number,
  initialMaxBid: number,
  timezone: string,
  upsell: bool,
  lastBudgetChangeActivity: string,
  creativeTypes: arrayOf(string),
  offerType: string,
  targeting: string,
  clmContentPreview: string,
};

export const campaignLineShape = shape(campaignLineProps);

// Campaign line after transformation for easier rendering
export const clCountryLineShape = shape({
  label: string.isRequired,
  lines: arrayOf(number).isRequired,
});

export const clProductLineShape = shape({
  label: string.isRequired,
  countries: arrayOf(clCountryLineShape).isRequired,
});

export const clBusinessLineShape = shape({
  label: string.isRequired,
  products: arrayOf(clProductLineShape).isRequired,
});

export const highlightedSkusShape = shape({
  [productCodes.CCP]: arrayOf(string),
});

export const campaignProductProps = {
  id: number.isRequired,
  code: string.isRequired,
  business: string,
  externalName: string,
  supportsCollectionIds: bool,
  supportsContentIds: bool,
  supportsTargeting: bool,
};

export const campaignProductShape = shape(campaignProductProps);

export const campaignProductMetaProps = {
  id: number,
  productId: number.isRequired,
  spend: number,
  creativeCollabaryId: string,
  creativeCampaignSetup: number,
  creativeConcept: number,
  creativeHandlingFee: number,
  creativeTalent: number,
  creativeDesign: number,
  creativeReach: number,
  creativeKsk: number,
  costPerShootingDay: number,
  additionalModelCost: number,
  videoUpgrade: number,
  locationUpgrade: number,
  advancedUsageRights: number,
  numberOfShootingDays: number,
  landingPageCost: number,
  mastercopyCost: number,
  numberOfURLs: number,
  collectionIds: arrayOf(string.isRequired),
  offsiteSpend: number,
  proposition: string,
  contentIds: arrayOf(string),
};

export const campaignProductMetaShape = shape(campaignProductMetaProps);

export const basicAccountShape = shape({
  id: number.isRequired,
  name: string.isRequired,
  creditorNumber: string,
  debitorNumber: string,
  taxNumber: string,
  businessPartnerId: string,
  eligibleForCampaigns: bool,
  eligibleForMonthlyBilling: bool,
  salesforceId: string,
});

export const campaignProps = {
  id: number,
  number: string.isRequired,
  brands: arrayOf(associatedBrandShape.isRequired),
  quote: associatedQuoteShape,
  opportunity: associatedOpportunityShape,
  owner: associatedUserShape,
  name: string.isRequired,
  nCode: string,
  status: oneOf(Object.values(campaignStatuses).map(ea => ea.value)),
  startDatePlan: string,
  endDatePlan: string,
  startDateIs: string,
  endDateIs: string,
  highlightedSkus: highlightedSkusShape,
  excludeDiscountedSKUs: bool,
  merchantExclusivity: string,
  useRecommendedColors: bool,
  filterOnSupplierCode: bool,
  steerOnSupplierCode: bool,
  collabaryId: string,
  adOpsDelayedStartReasons: arrayOf(string),
  adOpsDelayedStartReasonsUpdatedAt: string,
  pcDelayedStartReasons: arrayOf(string),
  pcDelayedStartReasonsUpdatedAt: string,
  delayedStartComment: string,
  opsOnsiteId: number,
  opsCreativeId: number,
  opsCiId: number,
  opsOtherId: number,
  created: string,
  lastModified: string,
  createdBy: string,
  lastModifiedBy: string,
  campaignLines: oneOfType([
    arrayOf(campaignLineShape),
    arrayOf(clBusinessLineShape),
  ]),
  draftDiff: draftShape,
  archivedDraftDiff: draftShape,
  draftOrigin: number,
  isDraft: bool,
  isActiveDraft: bool,
  collaborators: arrayOf(associatedUserShape.isRequired),
  pspElement: string,
  productMetas: arrayOf(campaignProductMetaShape),
  underPerformanceReasons: arrayOf(string.isRequired).isRequired,
  underPerformanceReasonsUpdatedAt: string,
  hasScalableBudget: bool.isRequired,
  outfitIds: arrayOf(string.isRequired).isRequired,
  billableBudgetMediaExternal: number,
  billableBudgetMediaInternal: number,
  billableBudgetMediaWKZ: number,
  billableBudgetCreativeExternal: number,
  billableBudgetCreativeInternal: number,
  billableBudgetCreativeWKZ: number,
  billableBudgetCIExternal: number,
  billableBudgetCIInternal: number,
  billableBudgetCIWKZ: number,
  billableBudgetOtherExternal: number,
  billableBudgetOtherInternal: number,
  billableBudgetOtherWKZ: number,
  commercialIncentive: string,
  netClientBudget: number,
  additionalDetails: string,
  changeRequest: number,
  coInvest: number,
  netClientBudgetExternal: number,
  freeService: number,
  spendableBudget: number,
  wkzBudget: number,
  headerDiscountPercent: number,
  prioScoreMetadata: object,
  updateError: object,
  targetKpi: string.isRequired,
  targetKpiValue: number.isRequired,
  collaboratorIds: arrayOf(number),
  zmsAmUuid: string,
  promoteOnlyDiscountedSKUs: bool,
  needsManualInvoicing: bool,
  pushedToSalesforce: string,
  account: basicAccountShape,
};

export const campaignShape = shape(campaignProps);

export const detailedCampaignProps = {
  ...campaignProps,
  collaborators: arrayOf(associatedUserShape),
};

export const detailedCampaignShape = shape(detailedCampaignProps);

export const basicBrandShape = shape({
  id: number.isRequired,
  name: string.isRequired,
  code: string,
});

export const basicOpportunityShape = shape({
  id: number.isRequired,
  name: string.isRequired,
  number: string.isRequired,
  salesUnit: string,
  useCase: string,
  reservationId: string,
});

export const basicQuoteShape = shape({
  id: number.isRequired,
  name: string.isRequired,
});

export const basicUserShape = shape({
  id: number.isRequired,
  firstName: string.isRequired,
  lastName: string.isRequired,
});

export const basicCampaignProps = {
  id: number.isRequired,
  name: string.isRequired,
  number: string.isRequired,
  nCode: string.isRequired,
  status: oneOf(Object.values(campaignStatuses).map(ea => ea.value)).isRequired,
  campaignLinesByStatus: object,
  coInvest: number,
  collaborators: arrayOf(associatedUserShape.isRequired),
  startDatePlan: string,
  endDatePlan: string,
  startDateIs: string,
  endDateIs: string,
  netClientBudget: number,
  netClientBudgetExternal: number,
  totalSpentBudget: number.isRequired,
  openBudget: number.isRequired,
  totalOffsiteSpentBudget: number.isRequired,
  spendableBudget: number.isRequired,
  wkzBudget: number,
  opportunity: basicOpportunityShape,
  owner: basicUserShape.isRequired,
  quote: basicQuoteShape.isRequired,
  opportunityId: number.isRequired,
  hasScalableBudget: bool.isRequired,
  commercialIncentive: oneOf(commercialIncentives),
  freeService: number,
  lastModified: string,
  opsCreative: associatedUserShape,
  opsOnsite: associatedUserShape,
  pendingChangeRequestsCount: number,
  targetKpi: string.isRequired,
  targetKpiValue: number.isRequired,
  salesChannel: string,
  account: basicAccountShape,
};

export const basicCampaignShape = shape(basicCampaignProps);

export const commercialPackageProps = {
  id: number.isRequired,
  value: string.isRequired,
  deprecated: string,
  created: string,
  lastModified: string,
  createdBy: string,
  lastModifiedBy: string,
  salesforceId: string,
};

export const commercialPackageShape = shape(commercialPackageProps);

export const basicZMSAMMerchantProps = {
  id: number,
  name: string.isRequired,
  merchantId: string.isRequired,
  sapId: number,
};

export const basicZMSAMMerchantShape = shape(basicZMSAMMerchantProps);

export const associatedContactListProps = {
  id: number.isRequired,
  name: string.isRequired,
  description: string.isRequired,
  created: string.isRequired,
  createdBy: string.isRequired,
  lastModified: string,
  lastModifiedBy: string,
};

export const associatedContactListShape = shape(associatedContactListProps);

export const contactProps = {
  id: number,
  brands: arrayOf(associatedBrandShape.isRequired),
  contactLists: arrayOf(associatedContactListShape.isRequired),
  firstName: string,
  lastName: string,
  title: string,
  email: string,
  mobile: string,
  phone: string,
  fax: string,
  status: string,
  created: string.isRequired,
  lastModified: string.isRequired,
  createdBy: string.isRequired,
  lastModifiedBy: string.isRequired,
  merchants: arrayOf(basicZMSAMMerchantShape.isRequired),
  alertSubscriptionStatus: string.isRequired,
  salesforceId: string,
  isSalesforceContact: bool.isRequired,
  isZDirectUser: bool.isRequired,
};

export const contactShape = shape(contactProps);

export const contactListProps = {
  id: number.isRequired,
  name: string.isRequired,
  description: string.isRequired,
  contactIds: arrayOf(number).isRequired,
  created: string,
  lastModified: string,
  createdBy: string,
  lastModifiedBy: string,
};

export const contactListShape = shape(contactListProps);

export const brandProps = {
  id: number,
  name: string.isRequired,
  code: string,
  urlKey: string,
  shopStatus: string,
  cluster: string,
  family: string,
  created: string.isRequired,
  isBrandGroup: bool.isRequired,
  lastModified: string.isRequired,
  createdBy: string.isRequired,
  lastModifiedBy: string.isRequired,
  zmsActive: bool,
  salesforceId: string,
};

export const brandShape = shape(brandProps);

export const zmsAMMerchantProps = {
  id: number,
  name: string,
  merchantId: string,
  pc: associatedUserShape,
  pcId: number,
  pcSalesUnit: string,
  salesTeam: string,
  salesUnit: string,
  framework: string,
  created: string,
  isSelfService: bool,
  contactIds: arrayOf(number.isRequired),
  hasTransitionDiscount: bool,
  eligibleForZMSAMPush: bool,
  isHybridPartner: bool,
  recordCreatedBy: string,
  merchantGroupName: string,
  merchantGroupId: string,
  merchantType: oneOf(Object.values(merchantTypesOptions).map(ea => ea.value)),
  companyName: string,
  vatId: string,
  supplierCode: string,
  countries: arrayOf(string.isRequired),
  sapId: number,
  invoiceBillingEntity: merchantShape,
  invoiceBillingEntityId: number,
  businessModel: string.isRequired,
};

export const zmsAMMerchantShape = shape(zmsAMMerchantProps);

export const opportunityProps = {
  billingType: string.isRequired,
  brands: arrayOf(associatedBrandShape.isRequired),
  brandIds: arrayOf(number.isRequired),
  brandCodes: arrayOf(string.isRequired),
  businessModel: string,
  campaignEnd: string.isRequired,
  campaign: associatedCampaignShape,
  campaignId: number,
  campaignObjective: oneOf(Object.values(campaignObjectives)).isRequired,
  campaignType: oneOf(Object.values(campaignTypes)).isRequired,
  campaignStart: string.isRequired,
  closed: string,
  collaboratorIds: arrayOf(number.isRequired),
  comment: string,
  created: string.isRequired,
  createdBy: string.isRequired,
  coInvest: number,
  coInvestType: string,
  commercialPackage: commercialPackageShape,
  netClientBudget: number,
  expectedRevenue: number,
  expectedRevenueMediaManaged: number,
  expectedRevenueMediaSelfService: number,
  expectedRevenueCreative: number,
  expectedRevenueCI: number,
  expectedRevenueOther: number,
  id: number,
  lastModified: string.isRequired,
  lastModifiedBy: string.isRequired,
  name: string.isRequired,
  negotiatedBy: string.isRequired,
  netClientBudgetExternal: number,
  number: string.isRequired,
  owner: associatedUserShape,
  ownerId: number,
  pc1: associatedUserShape,
  pc2: associatedUserShape,
  pc1Id: number,
  pc2Id: number,
  pool: string,
  subPool: string,
  salesUnit: string,
  salesTeam: string,
  primaryQuote: associatedQuoteShape,
  probability: number,
  relevanceMedia: number,
  relevanceCreative: number,
  relevanceCI: number,
  salesChannel: string.isRequired,
  stage: string.isRequired,
  useCase: string,
  creativePool: string.isRequired,
  selfServiceNCode: string,
  selfServiceBudget: number,
  selfServiceSpentBudget: number,
  selfServiceNetClientBudget: number,
  selfServiceFreeMedia: number,
  selfServiceCampaignStatus: string,
  spendableBudget: number,
  freeService: number,
  targetSuppliers: arrayOf(targetSupplierShape),
  targetSupplierIds: arrayOf(number),
  wkzBudget: number,
  excludeFromInvoiceAutomation: bool,
  spent: number,
  merchants: arrayOf(number),
  merchantIds: oneOfType([arrayOf(string), arrayOf(number)]),
  hasCCPCampaignLevelSteering: bool,
  hasCFCampaignLevelSteering: bool,
  salesforceId: string,
  associatedProductId: string,
  associatedProduct: associatedProductShape,
  reservationId: string,
  accountSfId: string,
  accountId: number,
  account: basicAccountShape,
};

export const opportunityShape = shape(opportunityProps);

export const detailedOpportunityProps = {
  ...opportunityProps,
  agreementOpportunities: arrayOf(agreementOpportunityShape.isRequired),
  budgets: object, // eslint-disable-line react/forbid-prop-types
  collaborators: arrayOf(associatedUserShape.isRequired),
};

export const detailedOpportunityShape = shape(detailedOpportunityProps);

export const countySplitProps = {
  id: number,
  countryCode: string.isRequired,
  share: number.isRequired,
  quoteLineId: number,
  created: string,
  lastModified: string,
  createdBy: string,
  lastModifiedBy: string,
};

export const countrySplitShape = shape(countySplitProps);

export const quoteLineProps = {
  id: number.isRequired,
  quoteId: number.isRequired,
  productId: number.isRequired,
  number: string,
  // lineNumber: number, // while sent, this is not used for anything
  startDate: string,
  endDate: string,
  genders: gendersType.isRequired,
  genderMenShare: number.isRequired,
  genderWomenShare: number.isRequired,
  genderKidsShare: number.isRequired,
  categories: arrayOf(string.isRequired),
  pricing: string,
  serviceDesc: string,
  netClientBudget: number.isRequired,
  netClientBudgetExternal: number.isRequired,
  wkzBudget: number.isRequired,
  coInvest: number,
  freeService: number,
  originalFee: number,
  adjustedFee: number,
  created: string.isRequired,
  lastModified: string.isRequired,
  createdBy: string.isRequired,
  lastModifiedBy: string.isRequired,
  countrySplit: oneOfType([
    arrayOf(countrySplitShape.isRequired).isRequired,
    shape({}), // object with countryCode string as key and share number as value
  ]).isRequired,
  secondaryCountryCodes: arrayOf(string.isRequired),
  productTag: string,
  grossOfferValue: number,
  isPartOfCommercialPackage: bool,
  lineNumber: number,
  netBudget: number,
  spendableBudget: number,
  salesforceId: string,
};

export const quoteLineShape = shape(quoteLineProps);

export const quoteLineBundleLine = shape({
  id: number.isRequired,
  productId: number.isRequired,
  countrySplit: arrayOf(countrySplitShape.isRequired).isRequired,
  pricing: string.isRequired,

  // One of "netClientBudget" and "netClientBudgetShare" is expected to be set.
  // If provided, "netClientBudget" takes precedence over "netClientBudgetShare".
  netClientBudget: number,
  netClientBudgetShare: number,

  startDate: string,
  endDate: string,
  genderMenShare: number,
  genderWomenShare: number,
  genderKidsShare: number,
  categories: arrayOf(string),

  created: string.isRequired,
  lastModified: string.isRequired,
  createdBy: string.isRequired,
  lastModifiedBy: string.isRequired,
});

export const quoteLineBundleProps = {
  id: number.isRequired,
  name: string.isRequired,
  lines: arrayOf(quoteLineBundleLine.isRequired).isRequired,
  created: string.isRequired,
  lastModified: string.isRequired,
  createdBy: string.isRequired,
  lastModifiedBy: string.isRequired,
};

export const quoteLineBundleShape = shape(quoteLineBundleProps);

export const quoteProps = {
  id: number.isRequired,
  opportunityId: number.isRequired,
  opportunity: associatedOpportunityShape,
  ownerId: oneOfType([number, string]).isRequired,
  comment: string,
  number: string,
  name: string.isRequired,
  additionalDetails: string,
  brandIds: arrayOf(number.isRequired),
  campaignId: number,
  quoteLines: arrayOf(quoteLineShape),
  headerDiscountPercent: number,
  flexibilityTerms: string.isRequired,
  allowedFlexibilities: arrayOf(string),
  purchaseOrderNumber: string,
  campaignObjective: string,
  grossOfferValue: number,
  freeServiceValue: number,
  feeDiscount: number,
  coInvest: number,
  netClientBudget: number,
  netClientBudgetExternal: number,
  spendableBudget: number,
  wkzBudget: number,
  targetKpi: string,
  targetKpiValue: number,
  created: string.isRequired,
  lastModified: string.isRequired,
  createdBy: string.isRequired,
  lastModifiedBy: string.isRequired,
  primaryContactId: number,
  secondaryContactId: number,
  billingContactId: number,
  billableBudgetMediaExternal: number,
  billableBudgetMediaInternal: number,
  billableBudgetMediaWKZ: number,
  billableBudgetCreativeExternal: number,
  billableBudgetCreativeInternal: number,
  billableBudgetCreativeWKZ: number,
  billableBudgetCIExternal: number,
  billableBudgetCIInternal: number,
  billableBudgetCIWKZ: number,
  billableBudgetOtherExternal: number,
  billableBudgetOtherInternal: number,
  billableBudgetOtherWKZ: number,
  commercialIncentive: string,
  salesforceId: string,
  accountId: number,
};

export const quoteShape = shape(quoteProps);

export const approvalTeamShape = shape({
  members: arrayOf(number.isRequired).isRequired,
});

export const attachmentContentTypeShape = oneOf([
  OPPORTUNITY,
  AGREEMENT,
  COMMENT,
]);

export const attachmentProps = {
  id: number,
  contentType: attachmentContentTypeShape.isRequired,
  objectId: number.isRequired,
  number: string,
  name: string.isRequired,
  type: oneOf(ALL_ATTACHMENT_TYPES),
  created: string.isRequired,
  lastModified: string.isRequired,
  createdBy: string.isRequired,
  lastModifiedBy: string.isRequired,
  awsFileKey: string.isRequired,
  awsFileVersionId: string.isRequired,
  fileFormat: string.isRequired,
  invoiceId: number,
  shaHash: string,
};

export const attachmentShape = shape(attachmentProps);

export const mentionProps = {
  id: number,
  startIndex: number,
  length: number,
  status: oneOf(['read', 'unread']),
  created: string,
  lastModified: string,
  createdBy: string,
  lastModifiedBy: string,
  commentId: number,
  userId: number,
};

export const mentionShape = shape(mentionProps);

export const changeApprovalShape = shape({
  id: number.isRequired,
  status: oneOf(Object.values(approvalStatuses)).isRequired,
  reviewerType: string.isRequired,
  reviewedBy: number,
});

export const changeRequestProps = {
  id: number.isRequired,
  campaignId: number,
  ownerId: number.isRequired,
  commentId: number.isRequired,
  type: oneOf(changeRequestTypes).isRequired,
  status: oneOf(Object.values(changeStatuses)).isRequired,
  approvals: arrayOf(changeApprovalShape.isRequired).isRequired,
  draftCampaignLines: arrayOf(campaignLineShape.isRequired).isRequired,
  draftCampaigns: arrayOf(campaignShape.isRequired).isRequired,
  created: string,
  lastModified: string,
  createdBy: string,
  lastModifiedBy: string,
  approvalRoles: arrayOf(string),
  salesforceId: string,
  targetCampaignId: number,
  targetDraftCampaignLines: arrayOf(campaignLineShape),
  upsellId: string,
};

export const changeRequestShape = shape(changeRequestProps);

export const commentProps = {
  id: number,
  senderId: number,
  created: string,
  lastModified: string,
  createdBy: string,
  lastModifiedBy: string,
  text: string,
  attachments: arrayOf(attachmentShape.isRequired),
  changeRequest: changeRequestShape,
  parentId: number,
  mentions: arrayOf(mentionShape.isRequired).isRequired,
  objectId: number,
  contentType: string,
};

export const commentShape = shape(commentProps);

export const notificationShape = shape({
  id: number,
  objectId: number,
  contentType: string,
  text: string,
  created: string,
  mentions: arrayOf(mentionShape.isRequired).isRequired,
});

export const productWithRequirementShape = shape({
  id: number.isRequired,
  name: string.isRequired,
  productGroup: string.isRequired,
  creativeRequirement: shape({
    id: number.isRequired,
    screenLabel: string.isRequired,
    sizeType: string.isRequired,
  }).isRequired,
});

export const creativeProps = {
  id: number.isRequired,
  created: string.isRequired,
  lastModified: string.isRequired,
  createdBy: string.isRequired,
  lastModifiedBy: string.isRequired,
  description: string,
  name: string.isRequired,
  origin: string.isRequired,
  genders: arrayOf(string).isRequired,
  fileUrl: string.isRequired,
  fileType: oneOf(Object.values(MEDIA_TYPES)).isRequired,
  fileSize: string.isRequired,
  fileFormat: string,
  imageWidth: number,
  imageHeight: number,
  products: shape({
    compatibleProducts: arrayOf(productWithRequirementShape.isRequired),
    noRequirementsProducts: arrayOf(
      shape({ id: number.isRequired, name: string.isRequired }).isRequired,
    ),
  }).isRequired,
  thumbnailUrl: string,
  countryCodes: arrayOf(string),
  categories: arrayOf(string),
  productTags: arrayOf(string),
  aspectRatio: string,
  duration: number,
  file: string,
  imageType: arrayOf(string.isRequired),
  languages: arrayOf(string.isRequired),
  mediaSet: string,
  opportunityId: number,
  productIds: arrayOf(number.isRequired),
};

export const creativeShape = shape(creativeProps);

export const campaignLineCreativeProps = {
  id: number.isRequired,
  created: string.isRequired,
  lastModified: string.isRequired,
  createdBy: string.isRequired,
  lastModifiedBy: string.isRequired,
  creativeId: number.isRequired,
  campaignLineId: number.isRequired,
};

export const campaignLineCreativeShape = shape(campaignLineCreativeProps);

export const associatedInvoiceProps = {
  id: number,
  created: string.isRequired,
  sapStatus: oneOf(['', ...invoiceSapStatuses.map(({ value }) => value)]),
  type: string.isRequired,
  billingType: oneOf(invoiceBillingTypes.map(({ value }) => value)).isRequired,
  status: oneOf(Object.values(invoiceStatuses).map(({ value }) => value))
    .isRequired,
};

export const associatedInvoiceShape = shape(associatedInvoiceProps);

export const bookingEntryProps = {
  id: number,
  invoice: associatedInvoiceShape,
  created: string.isRequired,
  lastModified: string.isRequired,
  createdBy: string.isRequired,
  lastModifiedBy: string.isRequired,
  debitAmount: number.isRequired,
  discount: number.isRequired,
  productId: number,
  entryType: string.isRequired,
  serviceStartDate: string,
  serviceEndDate: string,
  opportunity: associatedOpportunityShape,
};

export const bookingEntryShape = shape(bookingEntryProps);

export const invoiceProps = {
  id: number,
  opportunity: associatedOpportunityShape.isRequired,
  quote: associatedQuoteShape,
  owner: associatedUserShape,
  ownerId: number,
  ccInternalUser: associatedUserShape,
  ccInternalUserId: number,
  created: string.isRequired,
  lastModified: string.isRequired,
  createdBy: string.isRequired,
  lastModifiedBy: string.isRequired,
  name: string,
  purchaseOrderNumber: string,
  bookingEntries: arrayOf(bookingEntryShape),
  billingType: oneOf(invoiceBillingTypes.map(({ value }) => value)).isRequired,
  status: oneOf(Object.values(invoiceStatuses).map(({ value }) => value))
    .isRequired,
  cancellationReason: string,
  requestedChanges: string,
  additionalStatusInformation: string,
  sapInvoiceNumbers: arrayOf(string.isRequired),
  sapOrderNumber: string,
  sapReversalInvoiceNumbers: arrayOf(string.isRequired),
  sapPaidAt: string,
  sapStatus: oneOf(['', ...invoiceSapStatuses.map(({ value }) => value)]),
  type: string.isRequired,
  totalBillableBudget: number,
  totalInvoicedBudget: number,
  amount: number,
  nCode: string,
  lastExportDate: string,
  salesChannel: string,
  escalationStage: string,
  merchants: arrayOf(basicZMSAMMerchantShape),
  delayReasons: arrayOf(string),
  accountSfId: string,
};

export const invoiceShape = shape(invoiceProps);

export const invoiceCampaignProps = {
  id: number.isRequired,
  name: string.isRequired,
};

export const invoiceCampaignShape = shape(invoiceCampaignProps);

export const detailedInvoiceProps = {
  ...invoiceProps,
  campaign: invoiceCampaignShape,
};

export const detailedInvoiceShape = shape(detailedInvoiceProps);

export const offSiteMinBudgetShape = shape({
  baseBudget: number.isRequired,
  additionalBudgetPerCountry: number.isRequired,
});

/* More general shapes */

export const optionProps = {
  id: string,
  label: string.isRequired,
};

export const optionShape = shape(optionProps);

export const categoryProps = {
  label: oneOf(categoryLabels).isRequired,
  id: number.isRequired,
  options: arrayOf(optionShape),
};

export const categoryShape = shape(categoryProps);

export const errorShape = shape({
  message: string.isRequired,
  response: shape({ data: oneOfType([object, string]).isRequired }),
});

export const sessionShape = shape({
  token: string.isRequired,
  ttl: number.isRequired,
  user: userShape,
});

export const kpisProps = {
  totalROAS: number,
  pcROAS: number,
  pcSkuROAS: number,
  totalCPE: number,
  pcCPE: number,
  CPC: number,
  CTR: number,
  CPM: number,
  maxBid: number,
};

export const kpisShape = shape(kpisProps);

export const granularKpisProps = {
  ...kpisProps,
  aggregationLevel: number,
  productCode: string,
  countryCode: string,
};

export const granularKpisShape = shape(granularKpisProps);

export const kpiRecommendationProps = {
  avgKPIs: kpisShape,
  granularKPIs: arrayOf(granularKpisShape.isRequired),
  countryTotals: object,
  productTotals: object,
};

export const kpiRecommendationShape = shape(kpiRecommendationProps);

export const benchmarkRecommendationShape = shape({
  avgKPIs: kpisShape,
  granularKPIs: arrayOf(granularKpisShape.isRequired),
});

export const peerBrandsProps = {
  peerBrands: arrayOf(string.isRequired),
};

export const peerBrandsShape = shape(peerBrandsProps);

export const mediaTypesShape = oneOf(Object.values(MEDIA_TYPES));

export const reactTableColumnShape = shape({
  accessor: oneOfType([string, func]).isRequired,
  Header: oneOfType([string, func]).isRequired,
});

export const historyShape = shape({ push: func.isRequired });

export const announcementShape = shape({
  start: string.isRequired,
  end: string.isRequired,
  id: number.isRequired,
});

export const campaignStatusCountProps = {
  count: number.isRequired,
  status: oneOf(Object.values(campaignStatuses).map(ea => ea.value)).isRequired,
};

export const campaignStatusCountShape = shape(campaignStatusCountProps);

export const invoiceStatusCountProps = {
  count: number.isRequired,
  status: oneOf(Object.values(invoiceStatuses).map(ea => ea.value)).isRequired,
};

export const invoiceStatusCountShape = shape(invoiceStatusCountProps);

export const opportunityStageCountProps = {
  count: number.isRequired,
  stage: oneOf([
    ...Object.values(opportunityStages).map(ea => ea.value),
    'Agreed-current',
    'Agreed-archived',
  ]).isRequired,
};

export const opportunityStageCountShape = shape(opportunityStageCountProps);

export const campaignBudgetProps = {
  openBudget: number.isRequired,
  securedBudget: number.isRequired,
  securedPushedBudget: number.isRequired,
  openChangeRequests: number.isRequired,
};

export const campaignBudgetsShape = shape(campaignBudgetProps);

export const opportunityBudgetsProps = {
  openBudget: number.isRequired,
  securedBudget: number.isRequired,
  securedPushedBudget: number.isRequired,
  openChangeRequests: number,
};

export const opportunityBudgetsShape = shape(opportunityBudgetsProps);

export const entityShape = shape({
  id: oneOfType([number, string]).isRequired,
  Content: oneOfType([func, node]).isRequired,
});

export const billableBudgetShape = shape({
  billableBudgetMediaExternal: number.isRequired,
  billableBudgetMediaInternal: number.isRequired,
  billableBudgetMediaWKZ: number.isRequired,
  billableBudgetCreativeExternal: number.isRequired,
  billableBudgetCreativeInternal: number.isRequired,
  billableBudgetCreativeWKZ: number.isRequired,
  billableBudgetCIExternal: number.isRequired,
  billableBudgetCIInternal: number.isRequired,
  billableBudgetCIWKZ: number.isRequired,
  billableBudgetOtherExternal: number.isRequired,
  billableBudgetOtherInternal: number.isRequired,
  billableBudgetOtherWKZ: number.isRequired,
});

export const textBundleLineShape = shape({
  id: number,
  name: string.isRequired,
  campaignLineTextBundleItems: arrayOf(shape({})).isRequired,
});
